import {
  BASE_LINE_HEIGHT_MOD,
  COLORS,
  SHAPE_STYLES,
  TEXT_STYLE_TEXT_INPUT,
  TEXT_STYLE_TEXT_INPUT_INFO,
} from './constants';
import logger from './controllers/logger';
import { createDomObject } from './dom_object';
import {
  addGameObject,
  anchoredPos,
  createShapeStyleAnimation,
  getZeroAnchoredEntity,
  getZeroStaticEntity,
  setParent,
} from './entity';
import { cloneShapeStyle, colorToHex, Ease, v2 } from './helpers';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageItem,
  type RenderPackageRect,
} from './render_package';
import {
  type ServerEvent_TextInput,
  ObjectType,
  EventClientToServer,
  type JSON_TextInputValueChange,
} from './shared/api_shared_types';
import { setParentByEntityProps, setVisibilityState } from './simulate';
import * as textObject2 from './text_object2/text_object2';
import * as textObjectSimple from './textobject/textobject_simple';
import {
  AnimationType,
  RenderItemType,
  VisibilityState,
  type Entity,
  type GameManager,
  type GameObjectController,
  type RenderShapeStyle,
  type TextInput,
  type TextObject2,
  type TextObjectSimple,
  type V2,
} from './types';
import { createAndAddLocalTextObjectSimple } from './textobject/textobject_simple';

export function controller(): GameObjectController {
  return {
    getNew: getZeroTextInput,
    sync,
    update,
    createPackage,
  };
}

export function getZeroTextInput(ent: Entity): TextInput {
  return {
    ...ent,
    objectType: ObjectType.TEXT_INPUT,
    maxLength: 0,
    tObj: null,
    tObjTextLength: null,
    shapeStyle: cloneShapeStyle(SHAPE_STYLES.TEXT_INPUT.DEFAULT),
    _isEditing: false,
    value: '',
    locked: false,
  };
}

export function sync(manager: GameManager, obj: TextInput, props: ServerEvent_TextInput) {
  if (!obj.id) {
    addGameObject(manager, obj);
    initTextInput(manager, obj);
    setParentByEntityProps(manager, obj, props.sId, props.roomId, props.entityProps || {});
    setVisibilityState(obj, VisibilityState.DEFAULT);
  }

  if (props.width !== undefined) {
    obj.width = props.width;
  }
  if (props.height !== undefined) {
    obj.height = props.height;
  }
  if (props.maxLength !== undefined) {
    obj.maxLength = props.maxLength;
    if (obj.tObj !== null) {
      obj.tObj.maxLength = props.maxLength;
    }
  }
  if (props.value !== undefined) {
    obj.value = props.value;
    if (obj.tObj !== null) {
      textObject2.setText(obj.tObj, props.value);
    }
  }
  if (props.locked !== undefined && props.locked !== obj.locked) {
    obj.locked = props.locked;
    if (obj.tObj !== null) {
      obj.tObj.editMode = props.locked
        ? textObject2.TextObject2EditMode.OPEN_TO_SELECT
        : textObject2.TextObject2EditMode.OPEN_TO_EDIT;
    }
  }
}

function initTextInput(manager: GameManager, obj: TextInput) {
  obj.allowHighlight = true;
  obj.clickable = true;

  const props: Partial<TextObject2> = {
    editMode: textObject2.TextObject2EditMode.OPEN_TO_EDIT,
    text: '',
    textOverflow: 'wrap',
    relativeWidth: 1,
    relativeHeight: 1,
    padding: [20, 20, 20, 20],
    defaultTextStyle: TEXT_STYLE_TEXT_INPUT,
    passHover: true,
    canEditSize: false,
  };
  obj.tObj = textObject2.createAndAddLocalTextObject2(manager, getZeroStaticEntity(), props);

  setParent(obj.tObj, obj, v2());
}

export function update(_obj: TextInput): boolean {
  let newStyle: RenderShapeStyle = null;

  if (_obj.maxLength && !_obj.tObjTextLength) {
    const currentLength =
      _obj.tObj.editTextArea !== null ? _obj.tObj.editTextArea.value.length : _obj.tObj.text.length;
    const text = `${currentLength}/${_obj.maxLength}`;
    const props: Partial<TextObjectSimple> = {
      text,
      textOverflow: '',

      defaultTextStyle: TEXT_STYLE_TEXT_INPUT_INFO,
      passHover: true,
    };
    _obj.tObjTextLength = createAndAddLocalTextObjectSimple(
      _obj.manager,
      getZeroAnchoredEntity(),
      props,
    );

    _obj.tObjTextLength.anchoredPos = anchoredPos(v2(1, 1), v2(-7, 7), v2(1, 0));
    setParent(_obj.tObjTextLength, _obj);
  }

  if (_obj.tObj._isEditing) {
    if (_obj.maxLength) {
      const currentLength =
        _obj.tObj.editTextArea !== null
          ? _obj.tObj.editTextArea.value.length
          : _obj.tObj.text.length;
      textObjectSimple.setText(_obj.tObjTextLength, `${currentLength}/${_obj.maxLength}`);
    }

    if (!_obj._isEditing) {
      _obj._isEditing = true;

      newStyle = SHAPE_STYLES.TEXT_INPUT.ACTIVE;
    }
  } else {
    if (_obj._isEditing) {
      _obj._isEditing = false;

      _obj.value = _obj.tObj.text;

      if (_obj.highlighted) {
        newStyle = SHAPE_STYLES.TEXT_INPUT.HOVER;
      } else {
        newStyle = SHAPE_STYLES.TEXT_INPUT.DEFAULT;
      }
    } else {
      if (_obj.startHighlightThisFrame) {
        newStyle = SHAPE_STYLES.TEXT_INPUT.HOVER;
      } else if (_obj.stopHighlightThisFrame) {
        newStyle = SHAPE_STYLES.TEXT_INPUT.DEFAULT;
      }
    }
  }

  if (newStyle) {
    createShapeStyleAnimation(_obj.manager, _obj, {
      type: AnimationType.SHAPE_STYLE,
      end: newStyle,
      duration: 0.2,
      easeFunction: Ease.inOutQuad,
    });
  }

  return false;
}

export function createPackage(
  textInput: TextInput,
  position: V2,
  scale: number,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  let rect: RenderPackageRect = {
    type: RenderItemType.rect,
    ...EMPTY_RENDER_TRANSFORM,
    scale,
    positionX: position.x,
    positionY: position.y,
    width: textInput.width,
    height: textInput.height,
    opacity: textInput.opacity,

    shapeStyle: textInput.shapeStyle,
    cornerRadius1: 10,
    cornerRadius2: 10,
    cornerRadius3: 10,
    cornerRadius4: 10,
    shadow: null,
  };

  rPackage.push(rect);

  return rPackage;
}
